/**
 * manufactureSquareBbox — produce a centred square bbox around a
 * single tap point, used by AI tap-add (#411).
 *
 * The BE integration guide §2 recommends the half-side be **12% of
 * `min(naturalWidth, naturalHeight)`**. Smaller = more precise crop
 * but risk of clipping the object's edges. Larger = more context for
 * the model but risk of catching a neighbour.
 *
 * Output is in **natural image pixels** (NOT display pixels) — the
 * caller is responsible for converting tap coords to image space
 * before calling this. All fields are positive integers; the rect is
 * always fully inside the image (clamped to bounds; will shrink the
 * side rather than overflow when the tap is near a corner).
 */

import type { Bbox } from './useImageTransform';

export const DEFAULT_HALF_SIDE_RATIO = 0.12; // 12% — see BE guide §2

interface ManufactureOpts {
  /** Override the default 12% half-side ratio. Recommended range
   *  per the BE guide is [0.08, 0.18]. */
  halfSideRatio?: number;
}

/**
 * Build a square bbox centred on `(tapX, tapY)`, sized to
 * `2 * halfSideRatio * min(naturalW, naturalH)`. Clamped to image
 * bounds; when the tap is near an edge the rect is pulled inward
 * so it remains square AND fully inside the image (this matches
 * what BE-411 expects — it 400s on bboxes that overflow).
 *
 * @param tapX tap X in image-space pixels (NOT display).
 * @param tapY tap Y in image-space pixels.
 * @param naturalW image natural width in px (>0).
 * @param naturalH image natural height in px (>0).
 */
export function manufactureSquareBbox(
  tapX: number,
  tapY: number,
  naturalW: number,
  naturalH: number,
  opts: ManufactureOpts = {},
): Bbox {
  if (naturalW <= 0 || naturalH <= 0) {
    // Degenerate input — return a 1×1 at origin so the caller has a
    // valid shape rather than NaN. Should never happen in practice
    // (we only call this once the image has reported its natural
    // size) but be defensive.
    return { x: 0, y: 0, w: 1, h: 1 };
  }
  const ratio = opts.halfSideRatio ?? DEFAULT_HALF_SIDE_RATIO;
  // Compute the desired full side (2 * half-side).
  const minSide = Math.min(naturalW, naturalH);
  // Also cap the side to the smaller dimension so we never request a
  // box larger than the image itself (an image where naturalW=naturalH=100
  // shouldn't get a 360px box just because the ratio implies it).
  let side = Math.round(2 * ratio * minSide);
  if (side < 1) side = 1;
  if (side > naturalW) side = naturalW;
  if (side > naturalH) side = naturalH;

  // Centre on the tap, then pull inward so the rect fits.
  let x = Math.round(tapX - side / 2);
  let y = Math.round(tapY - side / 2);
  // Clamp top-left so the right/bottom edges don't overflow.
  if (x < 0) x = 0;
  if (y < 0) y = 0;
  if (x + side > naturalW) x = naturalW - side;
  if (y + side > naturalH) y = naturalH - side;

  return { x, y, w: side, h: side };
}
